import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import { useTranslation } from "react-i18next";
import {
  locationCardLines,
  locationCoordsForDisplay,
} from "./locationDisplay";
import { buildStaticMapPreviewUrl } from "../../utils/locationMapPreview";

const CARD_MAX_W = 280;
const MAP_H = 140;

/**
 * @param {{ message: object, presentation?: "whatsapp" | "default" }} props
 */
const LocationMessage = ({ message, presentation = "default" }) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const loc = message?.location;
  const coords = locationCoordsForDisplay(loc);
  const { primary, secondary } = locationCardLines(loc);
  const [mapFailed, setMapFailed] = useState(false);

  if (!primary && !coords) return null;

  const mapsUrl = coords
    ? `https://www.google.com/maps?q=${encodeURIComponent(String(coords.lat))},${encodeURIComponent(String(coords.lng))}`
    : null;

  const previewUrl =
    coords && !mapFailed
      ? buildStaticMapPreviewUrl(coords.lat, coords.lng, {
          width: CARD_MAX_W * 2,
          height: MAP_H * 2,
        })
      : null;

  const isWa = presentation === "whatsapp";
  const cardBg =
    theme.palette.mode === "dark" ? "rgba(18,18,18,0.98)" : "#ffffff";
  const titleColor =
    theme.palette.mode === "dark"
      ? theme.palette.grey[100]
      : theme.palette.grey[900];
  const subColor =
    theme.palette.mode === "dark"
      ? theme.palette.grey[400]
      : theme.palette.grey[700];
  const linkColor = theme.palette.mode === "dark" ? "#53bdeb" : "#039be5";

  const footer = (
    <Box sx={{ px: 1.25, py: 1, bgcolor: cardBg }}>
      {primary && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: isWa ? 600 : 500,
            color: titleColor,
            lineHeight: 1.35,
            wordBreak: "break-word",
          }}
        >
          {primary}
        </Typography>
      )}
      {secondary && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            mt: primary ? 0.35 : 0,
            color: subColor,
            lineHeight: 1.35,
            wordBreak: "break-word",
          }}
        >
          {secondary}
        </Typography>
      )}
      {mapsUrl && (
        <Typography
          component="span"
          sx={{
            display: "block",
            mt: 0.75,
            pt: 0.75,
            borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
            fontSize: "0.8125rem",
            fontWeight: isWa ? 500 : 400,
            color: linkColor,
          }}
        >
          {t("location.openInMaps")}
        </Typography>
      )}
    </Box>
  );

  const mapBlock = coords && (
    <Box
      sx={{
        position: "relative",
        height: MAP_H,
        bgcolor:
          theme.palette.mode === "dark" ? "grey.900" : "grey.200",
        overflow: "hidden",
      }}
    >
      {previewUrl ? (
        <Box
          component="img"
          src={previewUrl}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setMapFailed(true)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <PlaceRoundedIcon sx={{ fontSize: 40, color: "error.main", opacity: 0.9 }} />
        </Box>
      )}
    </Box>
  );

  const inner = (
    <Box
      sx={{
        maxWidth: CARD_MAX_W,
        borderRadius: 2,
        overflow: "hidden",
        border: (muiTheme) =>
          `1px solid ${
            muiTheme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)"
          }`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 1px 3px rgba(0,0,0,0.4)"
            : "0 1px 3px rgba(0,0,0,0.12)",
      }}
    >
      {mapBlock}
      {footer}
    </Box>
  );

  if (mapsUrl && coords) {
    return (
      <Box>
        {!isWa && (
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 0.5, opacity: 0.85 }}
          >
            {t("location.label")}
          </Typography>
        )}
        <Box
          component="a"
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          sx={{
            display: "inline-block",
            maxWidth: "100%",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          {inner}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {!isWa && primary && (
        <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
          {t("location.label")}
        </Typography>
      )}
      {inner}
    </Box>
  );
};

export default LocationMessage;
